import { Container, Graphics } from 'pixi.js';
import type { RoomContext, RoomDecorator } from './index';
import { place } from './index';
import { AO } from '../shading';
import { registerScenery, synthCtx } from '../scenery';

/**
 * TOILET — the office restroom, done tastefully (clean and cute, never gross).
 *
 * The vision: you step in from the NORTH (straight from reception) into a WIDE, SHALLOW washroom that
 * reads instantly as a real, well-kept facility — everything hugs the walls so the little centre stays
 * a clear tiled floor you can turn around in. Along the SOUTH wall, spread across the width: a row of
 * crisp white STALL dividers on the left, and on the EAST end a fully enclosed CUBICLE (the interactive
 * seat at 1480,1080, its door open to the west so you walk in and sit). A low VANITY with two recessed
 * basins, gooseneck taps and a soap pump sits against the south wall on the WEST side, a long horizontal
 * MIRROR on the wall above it. A tidy row of URINALS hangs on the WEST wall (well away from the
 * cubicle). A pale tiled wet-zone mat grounds the centre, a wall hand dryer and a paper-towel dispenser
 * add believable detail, and a couple of leafy plants soften the corners. Compact, calm, characterful.
 *
 * Geometry (zone x[1040..1560] y[1000..1160], 520×160, WIDE & SHORT, centre 1300,1080):
 *   - ONE door, on the NORTH edge → opening x[1240..1360], straight in from reception.
 *   - There is NO west door any more — the whole west/east/south perimeter is ours to dress.
 * The interior is only ~136 tall, so every fixture is LOW and WALL-HUGGING. We keep the north-door
 * lane (x[1240..1360], hugging the inner north face) OPEN so the entrance is never blocked, and leave
 * the room's centre as clear tiled floor. Everything themes off `art` so it re-skins per world, biased
 * toward clean pale ceramic. Built ONCE on map build/re-skin — no animation, no per-frame work, no
 * hot-loop allocation.
 */

/** A clean pale ceramic/tile tone biased off the theme wall — the washroom's signature crispness. */
function ceramic(ctx: RoomContext, t = 0.5): number {
  return ctx.mix(ctx.art.wall, 0xf2f4f7, t);
}

/**
 * A run of `count` stall dividers standing along a wall: slim white partition panels with a dark base
 * shadow + a faint vertical seam, plus a hint of a door gap between each. Origin at the run's left end,
 * panels rising "up" the screen (toward lower y) off the wall they stand on. Static, no animation.
 */
function stallRun(ctx: RoomContext, count: number, pitch: number, depth: number): Container {
  const { darken } = ctx;
  const c = new Container();
  const panel = ceramic(ctx, 0.62);
  const g = new Graphics();
  for (let i = 0; i <= count; i++) {
    const x = i * pitch;
    // contact shadow where the divider meets the floor
    g.ellipse(x, 4, 5, depth * 0.5 + 4).fill({ color: 0x000000, alpha: 0.16 });
  }
  for (let i = 0; i <= count; i++) {
    const x = i * pitch;
    // the divider panel: a slim slab projecting off the wall, lit front edge + shaded far edge
    g.roundRect(x - 3, -depth, 6, depth, 2)
      .fill(panel)
      .roundRect(x - 3, -depth, 2, depth, 2)
      .fill({ color: 0xffffff, alpha: 0.22 })
      .roundRect(x + 1, -depth, 2, depth, 2)
      .fill({ color: 0x000000, alpha: 0.08 });
  }
  // a faint stall-door line spanning each bay (the cubicle fronts), with a tiny latch dot
  for (let i = 0; i < count; i++) {
    const x0 = i * pitch + 3;
    const x1 = (i + 1) * pitch - 3;
    g.rect(x0, -depth + 2, x1 - x0, 2)
      .fill({ color: ceramic(ctx, 0.74), alpha: 0.9 })
      .circle((x0 + x1) / 2, -depth + 7, 1.4)
      .fill({ color: darken(ctx.art.accent, 0.1), alpha: 0.85 });
  }
  c.addChild(g);
  return c;
}

/**
 * The VANITY: a stone worktop on warm base cabinetry with `basins` recessed ceramic basins, gooseneck
 * taps, and a pump soap bottle. Origin at the run's TOP end; the run extends DOWN (+y) by `len`; the
 * counter faces LEFT (into the room) so it reads as standing against a wall on its right. The mirror is
 * drawn separately (it belongs on the wall, behind/right of the counter).
 */
function vanity(ctx: RoomContext, len: number, basins = 2): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const cab = mix(0x7a5733, art.wall, 0.24); // warm wood cabinetry
  const top = ceramic(ctx, 0.66); // pale stone top
  const steel = mix(0x9aa1ab, art.wall, 0.18);
  const w = 26; // counter depth (it stands against the wall, narrow into the room)
  c.addChild(new Graphics().rect(4, 2, w, len).fill({ color: 0x000000, alpha: 0.14 }));
  const g = new Graphics()
    // base cabinet
    .roundRect(0, 0, w, len, 3)
    .fill(cab)
    .roundRect(0, 0, 4, len, 3)
    .fill({ color: 0x000000, alpha: 0.1 })
    // kick-board shadow at the floor side (left, into the room) + a far-edge AO (away from key light)
    .rect(0, 0, 3, len)
    .fill(darken(cab, 0.3))
    .rect(w - 4, 0, 4, len)
    .fill({ color: 0x000000, alpha: AO * 0.5 })
    // stone worktop overhanging into the room, with a lit front lip
    .roundRect(-6, -3, w + 8, len + 6, 2)
    .fill(top)
    .roundRect(-6, -3, 3, len + 6, 2)
    .fill({ color: 0xffffff, alpha: 0.28 });
  // cabinet-door faces with pulls, repeated down the run
  const door = 36;
  for (let dy = 6; dy + door <= len - 6; dy += door + 5) {
    g.roundRect(8, dy, 14, door, 2)
      .fill(darken(cab, 0.1))
      .circle(11, dy + door / 2, 1.6)
      .fill(lighten(art.accent, 0.1));
  }
  c.addChild(g);
  // basins recessed into the top, evenly spaced, each with a gooseneck tap rising at the wall side
  for (let b = 0; b < basins; b++) {
    const by = len * ((b + 1) / (basins + 1));
    c.addChild(
      new Graphics()
        // oval basin recess
        .ellipse(7, by, 9, 7)
        .fill(darken(top, 0.16))
        .ellipse(7, by, 6.5, 5)
        .fill(lighten(top, 0.12))
        // a faint water sheen
        .ellipse(7, by, 4, 2.4)
        .fill({ color: 0xbfe6ff, alpha: 0.3 })
        // gooseneck tap arcing out from the wall (right) over the basin
        .moveTo(20, by)
        .bezierCurveTo(24, by, 24, by - 8, 14, by - 8)
        .stroke({ color: steel, width: 2.5, alpha: 0.95 })
        .circle(14, by - 8, 1.4)
        .fill(darken(steel, 0.2)),
    );
  }
  // a little pump soap bottle near the middle of the run
  const sy = len * 0.5;
  c.addChild(
    new Graphics()
      .roundRect(2, sy - 5, 7, 9, 2)
      .fill({ color: lighten(art.accent, 0.25), alpha: 0.9 })
      .roundRect(2, sy - 5, 7, 2, 2)
      .fill({ color: 0xffffff, alpha: 0.3 })
      .rect(5, sy - 9, 2, 4)
      .fill(darken(steel, 0.1)),
  );
  return c;
}

/**
 * A wide wall mirror with a soft frame and a faint sky/room reflection wash — hangs above the vanity.
 * `len` is its long dimension. `horizontal` lays it flat along a top wall (long axis on x) instead of
 * the default tall orientation (long axis on y, for a side wall). Origin centred on the run.
 */
function mirror(ctx: RoomContext, len: number, horizontal = false): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const frame = darken(art.wall, 0.16);
  const glass = mix(art.windowSky, 0xf2f4f7, 0.45); // pale, cool reflective glass
  const g = new Graphics();
  if (horizontal) {
    g
      // frame
      .roundRect(-len / 2 - 4, -4, len + 8, 12, 3)
      .fill(frame)
      // glass
      .roundRect(-len / 2, -1, len, 6, 2)
      .fill(glass)
      // a soft left highlight + a diagonal sheen so it reads as glass
      .roundRect(-len / 2, -1, len * 0.35, 6, 2)
      .fill({ color: 0xffffff, alpha: 0.16 })
      .moveTo(-len / 2 + 6, 4)
      .lineTo(-len / 2 + len * 0.55, 0)
      .stroke({ color: lighten(glass, 0.3), width: 1.5, alpha: 0.5 });
  } else {
    g
      // frame
      .roundRect(-4, -len / 2 - 4, 12, len + 8, 3)
      .fill(frame)
      // glass
      .roundRect(-1, -len / 2, 6, len, 2)
      .fill(glass)
      // a diagonal sheen streak so it reads as glass, plus a soft top highlight
      .roundRect(-1, -len / 2, 6, len * 0.35, 2)
      .fill({ color: 0xffffff, alpha: 0.16 })
      .moveTo(4, -len / 2 + 6)
      .lineTo(0, -len / 2 + len * 0.55)
      .stroke({ color: lighten(glass, 0.3), width: 1.5, alpha: 0.5 });
  }
  c.addChild(g);
  return c;
}

/** A wall-mounted hand dryer: a rounded chrome box with a nozzle slot and a tiny status light. */
function handDryer(ctx: RoomContext): Container {
  const { art, mix, darken } = ctx;
  const c = new Container();
  const chrome = mix(0xc4cad2, art.wall, 0.2);
  c.addChild(
    new Graphics()
      .roundRect(-9, -11, 18, 22, 5)
      .fill(chrome)
      .roundRect(-9, -11, 18, 6, 5)
      .fill({ color: 0xffffff, alpha: 0.22 })
      // air-outlet slot
      .roundRect(-6, 2, 12, 3, 1.5)
      .fill(darken(chrome, 0.35))
      // little active LED
      .circle(5, -5, 1.3)
      .fill({ color: art.glow, alpha: 0.9 }),
  );
  return c;
}

/**
 * A wall-mounted PAPER-TOWEL dispenser: a small rounded ceramic box with a dispensing lip and a folded
 * towel peeking out the bottom. Reads as the believable detail beside the basins. Origin at its centre.
 */
function paperTowel(ctx: RoomContext): Container {
  const { darken } = ctx;
  const c = new Container();
  const body = ceramic(ctx, 0.7);
  c.addChild(
    new Graphics()
      .roundRect(-8, -10, 16, 20, 4)
      .fill(body)
      .roundRect(-8, -10, 16, 5, 4)
      .fill({ color: 0xffffff, alpha: 0.25 })
      // dispensing slot
      .roundRect(-5, 7, 10, 2, 1)
      .fill(darken(body, 0.3))
      // a folded towel peeking out the bottom
      .rect(-5, 9, 10, 3)
      .fill({ color: 0xffffff, alpha: 0.85 }),
  );
  return c;
}

/**
 * A row of `count` URINALS hanging off a wall: rounded ceramic bowls with a flush plate above and a
 * soft contact shadow. Origin at the run's TOP end; the run extends DOWN (+y) by `count * pitch`. The
 * bowls face LEFT (into the room) so they read as fixed against a wall on their right.
 */
function urinalRun(ctx: RoomContext, count: number, pitch: number): Container {
  const { darken, lighten } = ctx;
  const c = new Container();
  const bowl = ceramic(ctx, 0.72);
  const g = new Graphics();
  for (let i = 0; i < count; i++) {
    const y = pitch / 2 + i * pitch;
    g
      // contact/shadow puddle behind the bowl
      .ellipse(2, y + 14, 9, 5)
      .fill({ color: 0x000000, alpha: 0.12 })
      // the rounded bowl body, lip lit on its inner edge
      .roundRect(-4, y - 11, 16, 24, 7)
      .fill(bowl)
      .roundRect(-4, y - 11, 16, 8, 7)
      .fill({ color: 0xffffff, alpha: 0.22 })
      // recessed basin well
      .ellipse(4, y, 5, 8)
      .fill(darken(bowl, 0.14))
      .ellipse(4, y - 1, 3, 5)
      .fill({ color: 0xbfe6ff, alpha: 0.22 })
      // a small flush plate up on the wall above the bowl
      .roundRect(2, y - 18, 8, 5, 1.5)
      .fill(lighten(bowl, 0.06))
      .circle(6, y - 15.5, 1.3)
      .fill({ color: ctx.art.accent, alpha: 0.7 });
  }
  c.addChild(g);
  return c;
}

/**
 * An enclosed CUBICLE — the private stall you actually sit in (the interactive toilet anchor). A
 * three-sided partition box (back + top + bottom panels) standing off a wall to its right, its FRONT
 * (left) side left open as the doorway so a duck can walk in. Inside: a ceramic toilet (tank + bowl +
 * lifted lid) facing LEFT toward the open door, plus a small wall-mounted roll holder. `w`,`h` set the
 * box footprint; origin at its TOP-LEFT (the open-door corner). Static, no animation.
 */
function cubicle(ctx: RoomContext, w: number, h: number): Container {
  const { darken, lighten, art } = ctx;
  const c = new Container();
  const panel = ceramic(ctx, 0.62);
  const wall = 5; // partition thickness
  const g = new Graphics();
  // floor contact shadow under the whole box
  g.roundRect(4, 6, w, h, 6).fill({ color: 0x000000, alpha: 0.12 });
  // pale tiled cubicle floor
  g.roundRect(2, 2, w - 4, h - 4, 4).fill({ color: ceramic(ctx, 0.5), alpha: 0.5 });
  // back (right) partition wall
  g.roundRect(w - wall, 0, wall, h, 2)
    .fill(panel)
    .roundRect(w - wall, 0, 2, h, 2)
    .fill({ color: 0xffffff, alpha: 0.22 });
  // top + bottom partition walls, leaving the LEFT side open as the doorway
  for (const py of [0, h - wall]) {
    g.roundRect(0, py, w, wall, 2)
      .fill(panel)
      .roundRect(0, py, w, 2, 2)
      .fill({ color: 0xffffff, alpha: 0.18 });
  }
  // a hint of a door jamb on the open (left) front edge, top + bottom nibs
  g.roundRect(0, 0, 3, wall + 4, 2).fill(darken(panel, 0.08));
  g.roundRect(0, h - wall - 4, 3, wall + 4, 2).fill(darken(panel, 0.08));
  c.addChild(g);

  // the toilet, set toward the front (open) side of the cubicle, facing LEFT (toward the doorway) so
  // the seated duck reads as sitting ON it; the tank stands behind it against the back (right) wall.
  const tx = w * 0.44;
  const ty = h / 2;
  const porcelain = ceramic(ctx, 0.82);
  c.addChild(
    new Graphics()
      // soft shadow under the pedestal
      .ellipse(tx, ty + 12, 14, 6)
      .fill({ color: 0x000000, alpha: 0.14 })
      // cistern/tank against the back (right) wall
      .roundRect(tx + 6, ty - 12, 12, 24, 3)
      .fill(porcelain)
      .roundRect(tx + 6, ty - 12, 12, 5, 3)
      .fill({ color: 0xffffff, alpha: 0.3 })
      // flush button on top of the tank
      .roundRect(tx + 10, ty - 3, 4, 6, 1.5)
      .fill(darken(porcelain, 0.18))
      // the bowl, an oval pointing LEFT into the room
      .ellipse(tx - 4, ty, 13, 10)
      .fill(porcelain)
      // the lifted lid / seat ring catching light
      .ellipse(tx - 4, ty, 9, 6.5)
      .fill(lighten(porcelain, 0.1))
      .ellipse(tx - 4, ty, 6, 4)
      .fill({ color: 0xbfe6ff, alpha: 0.28 })
      // pedestal base
      .roundRect(tx - 2, ty + 6, 10, 8, 2)
      .fill(darken(porcelain, 0.06)),
  );
  // a small wall-mounted toilet-roll holder on the back wall
  c.addChild(
    new Graphics()
      .roundRect(w - wall - 4, ty + 6, 8, 7, 2)
      .fill({ color: 0xffffff, alpha: 0.9 })
      .circle(w - wall, ty + 9.5, 2)
      .fill(darken(art.accent, 0.05)),
  );
  return c;
}

/** A slim wooden BENCH against a wall — somewhere to set a bag or sit a moment. Origin at its centre. */
function bench(ctx: RoomContext, len: number): Container {
  const { mix, art, darken, lighten } = ctx;
  const c = new Container();
  const wood = mix(0x8a6536, art.wall, 0.22);
  const g = new Graphics();
  g.ellipse(0, len / 2 + 3, 10, 5).fill({ color: 0x000000, alpha: 0.12 });
  g.ellipse(0, -len / 2 - 3, 10, 5).fill({ color: 0x000000, alpha: 0.12 });
  // seat slab running along the wall (long axis on y)
  g.roundRect(-9, -len / 2, 18, len, 4)
    .fill(wood)
    .roundRect(-9, -len / 2, 5, len, 4)
    .fill({ color: 0xffffff, alpha: 0.14 })
    .roundRect(4, -len / 2, 5, len, 4)
    .fill({ color: 0x000000, alpha: 0.12 });
  // a couple of plank seams
  for (let py = -len / 2 + len / 3; py < len / 2; py += len / 3) {
    g.moveTo(-8, py).lineTo(8, py).stroke({ color: darken(wood, 0.2), width: 1, alpha: 0.4 });
  }
  // two little legs
  g.rect(-7, len / 2 - 4, 3, 5).fill(darken(wood, 0.3));
  g.rect(4, len / 2 - 4, 3, 5).fill(darken(wood, 0.3));
  g.rect(-7, -len / 2 - 1, 3, 5).fill(darken(wood, 0.3));
  g.rect(4, -len / 2 - 1, 3, 5).fill(darken(wood, 0.3));
  void lighten;
  c.addChild(g);
  return c;
}

registerScenery('rest-bench', (art) => bench(synthCtx(art), 52), 'Rest bench', '🛋️');

/**
 * A pale TILED MAT: a soft grid of ceramic tiles grounding the wet zone in the room centre. Flat on
 * the floor (no contact shadow), themed pale. `w`,`h` set its footprint; origin at its centre.
 */
function tiledMat(ctx: RoomContext, w: number, h: number): Container {
  const { darken } = ctx;
  const c = new Container();
  const tile = ceramic(ctx, 0.58);
  const grout = darken(tile, 0.18);
  const g = new Graphics().roundRect(-w / 2, -h / 2, w, h, 6).fill({ color: tile, alpha: 0.55 });
  const step = 18;
  for (let gx = -w / 2 + step; gx < w / 2; gx += step) {
    g.moveTo(gx, -h / 2 + 3).lineTo(gx, h / 2 - 3);
  }
  for (let gy = -h / 2 + step; gy < h / 2; gy += step) {
    g.moveTo(-w / 2 + 3, gy).lineTo(w / 2 - 3, gy);
  }
  g.stroke({ color: grout, width: 1, alpha: 0.4 });
  // a soft inset border so the mat reads as a deliberate tiled zone
  g.roundRect(-w / 2 + 4, -h / 2 + 4, w - 8, h - 8, 4).stroke({ color: grout, width: 1.5, alpha: 0.35 });
  c.addChild(g);
  return c;
}

const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { zone, art, f } = ctx;
  const left = zone.x; // 1040
  const top = zone.y; // 1000
  const bottom = zone.y + zone.height; // 1160

  // ONE door, on the NORTH edge: opening x[1240..1360], straight in from reception. There is NO west
  // door any more. Keep the north-door lane (x[1240..1360], hugging the inner north face) clear so the
  // entrance threshold stays bare floor — no fixture intrudes on that strip.

  // ── TILED WET-ZONE MAT centred in the room, grounding the small open floor between the fixtures. ───
  // Footprint ~x[1252..1348] × y[1052..1108]; sits in the open centre just south of the door lane and
  // clear of every wall fixture, so the middle reads as a deliberate tiled turn-around space.
  place(ctx, tiledMat(ctx, 96, 56), left + 260, top + 80);

  // ── ENCLOSED CUBICLE on the EAST end of the SOUTH wall — the interactive seat. Anchor (1480,1080)
  // sits INSIDE, facing WEST so the seated duck reads as sitting on the bowl. ─────────────────────────
  // Box origin = top-left/open-door corner at (1442,1025); footprint 96×110 → box x[1442..1538] (back
  // wall 22px off the inner east face) and y[1025..1135] (25px off the south wall, the door open to the
  // west). The toilet inside (tx=0.44w → ~1481, ty=h/2 → 1080) lands its bowl at ~(1480,1080), exactly
  // under the anchor; the front (left) side is the open doorway you walk in through.
  place(ctx, cubicle(ctx, 96, 110), left + 402, top + 25);

  // ── LOW VANITY on the SOUTH wall, WEST side: two basins + taps + soap, lying flat along the wall. ──
  // The down-running counter is rotated -90° to lie along the south wall; with that rotation local +y
  // maps to world +x, so the placed origin is the run's WEST (left) end and the body extends EAST.
  // Run x[~1060..1160] (len 100); the counter body maps to world y[~1106..1140], hugging the inner
  // south face (y=1160), the basins/taps opening NORTH into the room. A horizontal MIRROR hangs above.
  const vanLeft = left + 20; // ~1060 — west end of the counter, clear of the SW corner
  const vanLen = 100; // east end ~1160, a margin before the stall run
  const vanY = bottom - 30; // ~1130 — anchors the counter body against the inner south face
  const vanCounter = vanity(ctx, vanLen, 2);
  vanCounter.rotation = -Math.PI / 2; // lay the down-running counter flat along the south wall
  place(ctx, vanCounter, vanLeft, vanY); // rotated origin maps to the run's WEST (left) end
  // Wide mirror on the wall above the counter, centred on the run, its frame ending on the south face.
  place(ctx, mirror(ctx, vanLen - 12, true), vanLeft + vanLen / 2, bottom - 9);

  // ── STALL RUN along the SOUTH wall, between the vanity and the cubicle: three dividers / two stalls. ─
  // Low dividers (depth 24) project UP off the inner south face; panels at pitch 80 give run x[~1177..
  // 1423], base y=1140 with its contact shadow ending exactly on the south face (y=1160). East of the
  // vanity (ends ~1160) and west of the SE cubicle (1442), leaving the room's centre open floor.
  place(ctx, stallRun(ctx, 3, 80, 24), left + 140, bottom - 20);

  // ── URINAL ROW on the WEST wall (well away from the SE cubicle). Bowls face EAST into the room. ─────
  // urinalRun's bowls face LEFT with their back on the right; we mirror it on x so the backs hug the
  // inner west face and the bowls open into the room. Two bowls span y[~1014..1098] (pitch 42), their
  // backs landing on the inner west face ~1044.
  const urinals = urinalRun(ctx, 2, 42);
  urinals.scale.x = -1; // flip so bowls open EAST (into the room) off the west wall
  place(ctx, urinals, left + 16, top + 14);

  // ── HAND DRYER on the NORTH wall, WEST of the door lane (clear of the urinals on the west wall). ─────
  // Centred ~(1140,1016), hard against the inner north face — a tidy place to dry off as you head out.
  place(ctx, handDryer(ctx), left + 100, top + 16);

  // ── PAPER-TOWEL dispenser on the SOUTH wall, above the vanity (right by the basins) so it sits right
  // where you'd dry your hands. ──────────────────────────────────────────────────────────────────────
  place(ctx, paperTowel(ctx), vanLeft + vanLen - 8, bottom - 18);

  // ── A slim BENCH on the NORTH wall in the gap EAST of the door lane (x>1360) and WEST of the cubicle
  // (x<1442). bench() runs its long axis on y, so we rotate it 90° to lie flat along the north wall:
  // centred ~(1400,1014), seat x[~1366..1434], hugging the inner north face — a tidy spot to set a bag
  // on the way in, well clear of the entrance threshold. ──────────────────────────────────────────────
  const benchPiece = bench(ctx, 52);
  benchPiece.rotation = Math.PI / 2; // lay the long axis flat along the (horizontal) north wall
  place(ctx, benchPiece, left + 360, top + 14, 'rest-bench');

  // ── Leafy PLANTS to spread a little green and break up the floor without crowding any lane. ────────
  // A tall plant in the NW band (east of the urinals, north of the vanity); a low fern in the open
  // centre-east floor (east of the mat, west of the cubicle) — both well clear of the door lane.
  place(ctx, f.plant(art, 'tall'), left + 45, top + 58);
  place(ctx, f.plant(art, 'fern'), left + 355, top + 75);
};

export default decorate;
